import React from 'react';
import { sidebar } from '../../../assets/theme/tokens';
import Drawer, { DrawerProps } from '@mui/material/Drawer';
import { useTheme } from '@mui/material/styles';
import { FFTheme } from 'assets/theme';

type SidenavRootProps = DrawerProps & {
  ownerState: {
    transparentSidenav: boolean;
    whiteSidenav: boolean;
    miniSidenav: boolean;
    darkMode: boolean;
  };
};

const SidenavRoot = ({ ownerState, ...rest }: SidenavRootProps) => {
  const theme = useTheme<FFTheme>();
  const { palette, boxShadows, transitions, breakpoints, functions } = theme;
  const { transparentSidenav, whiteSidenav, miniSidenav, darkMode } =
    ownerState;

  // The steerage app's sidebar width (tokens.sidebar.width).
  const sidebarWidth = sidebar.width;
  const { transparent, gradients, white, background, grey } = palette as any;
  const { xs } = boxShadows;
  const { pxToRem, linearGradient } = functions;

  let backgroundValue = darkMode
    ? background.sidenav
    : linearGradient(gradients.dark.main, gradients.dark.state);

  if (transparentSidenav) {
    backgroundValue = transparent.main;
  } else if (whiteSidenav) {
    backgroundValue = white.main;
  }

  const drawerOpenStyles = {
    margin: '8px',
    borderRadius: '8px',
    background: backgroundValue,
    transform: 'translateX(0)',
    transition: transitions.create('transform', {
      easing: transitions.easing.sharp,
      duration: transitions.duration.shorter,
    }),
    [breakpoints.up('xl')]: {
      boxShadow: transparentSidenav ? 'none' : xs,
      marginBottom: transparentSidenav ? 0 : 'inherit',
      left: '0',
      width: sidebarWidth,
      transform: 'translateX(0)',
      transition: transitions.create(['width', 'background-color'], {
        easing: transitions.easing.sharp,
        duration: transitions.duration.enteringScreen,
      }),
    },
  };

  const drawerCloseStyles = {
    background: backgroundValue,
    transform: `translateX(${pxToRem(-320)})`,
    transition: transitions.create('transform', {
      easing: transitions.easing.sharp,
      duration: transitions.duration.shorter,
    }),
    [breakpoints.up('xl')]: {
      boxShadow: transparentSidenav ? 'none' : xs,
      marginBottom: transparentSidenav ? 0 : 'inherit',
      left: '0',
      width: pxToRem(96),
      overflowX: 'hidden',
      transform: 'translateX(0)',
      transition: transitions.create(['width', 'background-color'], {
        easing: transitions.easing.sharp,
        duration: transitions.duration.shorter,
      }),
    },
  };

  return (
    <Drawer
      {...rest}
      sx={{
        '& .MuiDrawer-paper': {
          boxShadow: xs,
          border: transparentSidenav
            ? 'none'
            : `1px solid ${darkMode ? grey[800] : grey[200]}`,
          ...(miniSidenav ? drawerCloseStyles : drawerOpenStyles),
          padding: '0 10px'
        },
        ...(rest && rest.sx || {}),
      }}
    />
  );
};

export { SidenavRoot };
